import { useEffect, useState } from "react";
import { MessageCircle, Fuel, Cog, Users, Loader2, Star } from "lucide-react";
import { api, waLink, resolveImg } from "../../lib/site";
import { Reveal, Overline } from "./ui";

const filters = [
  { key: "all", label: "All Vehicles" },
  { key: "car", label: "Cars & SUVs" },
  { key: "bike", label: "Bikes & Scooters" },
];

export default function Showcase() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [price, setPrice] = useState("any");
  const [trans, setTrans] = useState("any");

  useEffect(() => {
    api
      .get("/vehicles")
      .then((res) => setVehicles(res.data))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);

  const priceValue = (v) => {
    const raw = (v.price_24hr || v.price_12hr || "").split("/")[0];
    const num = parseInt(raw.replace(/[^0-9]/g, ""), 10);
    return isNaN(num) ? 0 : num;
  };

  const matchPrice = (v) => {
    const p = priceValue(v);
    if (price === "lt2000") return p > 0 && p < 2000;
    if (price === "2000to3000") return p >= 2000 && p <= 3000;
    if (price === "gt3000") return p > 3000;
    return true;
  };

  const matchTrans = (v) => {
    if (trans === "any") return true;
    return (v.spec || "").toLowerCase().includes(trans);
  };

  const shown = vehicles
    .filter((v) => (active === "all" ? true : v.category === active))
    .filter(matchPrice)
    .filter(matchTrans);
  const featured = vehicles.filter((v) => v.featured);
  const filtersActive = active !== "all" || price !== "any" || trans !== "any";

  return (
    <section id="vehicles" className="relative bg-ink-surface px-5 py-24 md:px-10 md:py-32">
      <div className="mx-auto max-w-[1400px]">
        {!loading && featured.length > 0 && (
          <div className="mb-16" data-testid="featured-strip">
            <Reveal className="mb-6 flex items-center gap-3">
              <Star size={16} className="fill-volt text-volt" />
              <Overline>Featured — Top Picks</Overline>
              <span className="h-px flex-1 bg-white/10" />
            </Reveal>
            <div className="flex snap-x gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {featured.map((v, i) => (
                <Reveal key={v.id} delay={(i % 4) * 0.06} className="w-[280px] shrink-0 snap-start sm:w-[320px]">
                  <div
                    data-testid={`featured-card-${v.id}`}
                    className="group relative h-full overflow-hidden border border-volt/30 bg-ink shadow-[0_0_0_1px_rgba(214,255,0,0.15)] transition-colors hover:border-volt/60"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={resolveImg(v.image_url)} alt={v.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 bg-volt px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink">
                        <Star size={11} className="fill-ink" /> Featured
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <h3 className="truncate font-heading text-base font-bold text-white">{v.name}</h3>
                        <p className="text-xs text-white/50">{v.body_type} · {v.spec}</p>
                        <p className="mt-1 font-heading text-sm font-bold text-volt">{v.price_24hr}</p>
                      </div>
                      <a
                        href={waLink(`Hi, I'm interested in the featured ${v.name} (${v.body_type}).`)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`featured-book-${v.id}`}
                        aria-label={`Enquire about ${v.name}`}
                        className="flex h-11 w-11 shrink-0 items-center justify-center bg-volt text-ink transition-colors hover:bg-volt-hover"
                      >
                        <MessageCircle size={18} />
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        <div className="mb-12 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <Reveal className="max-w-2xl">
            <Overline>Available Now</Overline>
            <h2 className="mt-4 font-heading text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl md:text-5xl">
              Pick your ride
            </h2>
            <p className="mt-4 text-sm text-white/60 md:text-base">
              Every vehicle is checked, cleaned and ready. Tap a card to confirm availability for your dates over WhatsApp.
            </p>
          </Reveal>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setActive(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`border px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    active === f.key
                      ? "border-volt bg-volt text-ink"
                      : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="filter-price"
                className="border border-white/15 bg-ink px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-white/80 outline-none focus:border-volt [color-scheme:dark]"
              >
                <option value="any">Any price</option>
                <option value="lt2000">Under ₹2,000</option>
                <option value="2000to3000">₹2,000 – ₹3,000</option>
                <option value="gt3000">Over ₹3,000</option>
              </select>
              <select
                value={trans}
                onChange={(e) => setTrans(e.target.value)}
                data-testid="filter-transmission"
                className="border border-white/15 bg-ink px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-white/80 outline-none focus:border-volt [color-scheme:dark]"
              >
                <option value="any">Any transmission</option>
                <option value="manual">Manual</option>
                <option value="automatic">Automatic</option>
              </select>
              {filtersActive && (
                <button
                  onClick={() => { setActive("all"); setPrice("any"); setTrans("any"); }}
                  data-testid="filter-clear"
                  className="border border-white/15 px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-volt hover:border-volt"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="vehicles-loading">
            <Loader2 className="animate-spin text-volt" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`vehicle-card-${v.id}`}
                  className="group flex h-full flex-col border border-white/10 bg-ink transition-colors duration-300 hover:border-volt/40"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={resolveImg(v.image_url)}
                      alt={v.name}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 bg-volt px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink">
                      {v.body_type || (v.category === "car" ? "Car" : "Bike")}
                    </span>
                    {!v.available && (
                      <span className="absolute right-3 top-3 bg-destructive px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                        Booked
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-xl font-bold tracking-tight text-white">{v.name}</h3>
                    <p className="mt-1 text-sm text-white/50">{v.description}</p>

                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/70">
                      <span className="inline-flex items-center gap-1.5"><Fuel size={13} className="text-volt" /> {v.fuel}</span>
                      <span className="inline-flex items-center gap-1.5"><Cog size={13} className="text-volt" /> {v.spec}</span>
                      <span className="inline-flex items-center gap-1.5"><Users size={13} className="text-volt" /> {v.seating}</span>
                    </div>

                    <div className="mt-5 border-t border-white/10 pt-4">
                      {v.price_12hr && (
                        <div className="text-sm font-semibold text-white">{v.price_12hr}</div>
                      )}
                      <div className="font-heading text-lg font-bold text-volt">{v.price_24hr}</div>
                    </div>

                    <a
                      href={waLink(`Hi, I'd like to check availability for the ${v.name} (${v.body_type}).`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`vehicle-book-${v.id}`}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-white/5 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-volt hover:text-ink"
                    >
                      <MessageCircle size={15} /> Check Availability
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
            {shown.length === 0 && (
              <p className="col-span-full py-16 text-center text-white/50" data-testid="no-vehicles-message">No vehicles match these filters. Try adjusting the price or transmission.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
